import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { makeStyles, Typography } from '@material-ui/core';
import { useEffect } from 'react';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  repoName: {
    fontSize: '1.25rem',
    fontWeight: 700,
    fontFamily: 'monospace',
    color: theme.palette.text.primary,
    wordBreak: 'break-all',
  },
  repoNameError: {
    fontSize: '1.25rem',
    fontWeight: 700,
    fontFamily: 'monospace',
    color: theme.palette.error.main,
    wordBreak: 'break-all',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  placeholder: {
    fontStyle: 'italic',
    color: theme.palette.text.disabled,
  },
}));

interface StepFormData {
  systemName?: string;
  componentName?: string;
  suffixName?: string;
}

/**
 * Computes the repository name from system, component, and optional suffix.
 * Parts are joined with underscores, empty/undefined parts are excluded.
 */
export function computeRepositoryName(systemName?: string, componentName?: string, suffixName?: string): string {
  return [systemName, componentName, suffixName].filter(Boolean).join('_');
}

/**
 * A read-only scaffolder field extension that displays the computed
 * repository name based on systemName, componentName, and suffixName
 * fields in the same form step.
 *
 * The computed value is synced to the form via onChange, so it appears
 * in the Review step and is available as a template parameter.
 */
export const RepositoryNamePreview = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, formContext, uiSchema } = props;
  const classes = useStyles();

  const uiOptions = uiSchema?.['ui:options'] as { label?: string; maxLength?: number } | undefined;
  const label = uiOptions?.label || 'Repository Name';
  const maxLength = uiOptions?.maxLength ?? 64;

  const { systemName, componentName, suffixName } = (formContext?.formData as StepFormData) ?? {};

  const repositoryName = computeRepositoryName(systemName, componentName, suffixName);

  useEffect(() => {
    const newValue = repositoryName || undefined;
    if (newValue !== formData) {
      onChange(newValue);
    }
  }, [repositoryName, formData, onChange]);

  const hasInput = Boolean(systemName) || Boolean(componentName);

  if (!hasInput) {
    return (
      <div className={classes.container}>
        <Typography className={classes.label}>{label} (preview)</Typography>
        <Typography className={classes.placeholder}>
          Your {label.toLowerCase()} will appear here once you provide the System Name and Component Name above. Suffix
          Name is optional.
        </Typography>
      </div>
    );
  }

  const isOverLimit = repositoryName.length > maxLength;

  return (
    <div className={classes.container}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography
        className={isOverLimit ? classes.repoNameError : classes.repoName}
        data-testid="repository-name-value"
      >
        {repositoryName}
      </Typography>
      {isOverLimit && (
        <Typography className={classes.errorText}>
          The name must not exceed {maxLength} characters. Current length: {repositoryName.length}.
        </Typography>
      )}
    </div>
  );
};
